
import { Layers, isTextureIsland, textureIslandName, designLayersFrom,
         designFiguresFrom, docIslandsFrom, filterIslandsFor,
         modelToDocMatrix, filterTargetLayers } from 'cdl';

import { retargetEvent3D } from './move3d';


function notTextureIsland( figure ) {
  return ! isTextureIsland(figure);
}

export const Doc3D_Model_Mixin = {

  Properties: {
    selectableIslands: { type: 'boolean', def: true }
  },

  init() {
    this.callParent(...arguments);

    // DocIslands can not be moved from these views
    this.condition('movable'  ,notTextureIsland);
    this.condition('rotatable',notTextureIsland);
    this.condition('removable',notTextureIsland);
    this.condition('copyable' ,notTextureIsland);

  },

  activeSelectableFigures() {
    // [TODO] activePageSelection, allowCrossPageSelection
    return this.callParent();
  },

  uvToModel( uv ) {
    return this.model.uvToModel( uv );
  },

  uvToDoc( uv, docIsland ) {
    return this.modelToDoc( this.uvToModel(uv), docIsland );
  },

  modelToDoc( modelPoint, docIsland ) {
    if( docIsland === undefined ) {
      docIsland = this.topDocIslandAtModel(modelPoint);
      if( ! docIsland ) {
        return undefined;
      }
    }
    const matrix = this.model.modelToDocMatrixFor(docIsland);
    const docPoint = matrix.apply( modelPoint );
    return docPoint;
  },

  topDocIslandAtModel( modelPoint, modelIsland ) {
    if( ! modelIsland ){
      modelIsland = this.modelIslandAt(modelPoint);
    }
    return modelIsland && this.topDocIslandFor(modelIsland);
  },

  topDocIslandFor( modelIsland ) {
    return this.doc.topDocIslandFor(modelIsland);
  },

  docIslandsFor( docIsland ) {
    return this.doc.docIslandsFor(docIsland);
  },

  modelIslandFor( docIsland ) {
    return this.model.modelIslandFor( docIsland );
  },

  modelIslandAt( modelPoint ) {
    return this.textureView._modelReady && this.model.modelIslandAt( modelPoint );
  },

  modelPointIsOut( modelPoint, modelIsland ) {
    return ! modelIsland.bounds_().contains(modelPoint);
  },

  forEachDesignFiguresIn( name, func ) {
    const { doc } = this;
    const { pages } = doc;
    for( let p = 0, pEnd = pages.length; p < pEnd; ++p ) {
      const page = pages[p];
      const designFigures = designFiguresFrom(page);
      const docIslands = docIslandsFrom(page);
      for( let i = 0, iEnd = docIslands.length; i < iEnd; ++i ) {
        const docIsland = docIslands[i];
        if( textureIslandName(docIsland) === name ) {
          func(docIsland,designFigures);
        }
      }
    }
  },

  extractHitInfo( hit, info ) {
    if( hit ) {
      if( info ) {
        info.tight     = hit.tight;
        info.docPoint  = hit.docPoint;
        info.docIsland = hit.docIsland;
      }
      return hit.figure;
    }
    return hit;
  },

  figureAtModelPointEx( modelPoint, tabFigures ) {
    const candidates = this.candidateFiguresAtModelPoint(modelPoint);
    return this.bestFigureCandidate(candidates,tabFigures);
  },

  filterSelectableInto( figures, r ) { // [TODO] filter by condition
    for( let k = 0, kEnd = figures.length; k < kEnd; k++ ) {
      const figure = figures[k];
      if( this.is( figure, 'selectable' ) ) {
        r.push( figure );
      }
    }
  },

  rawFigureHitsAtModelPoint( modelPoint ) {
    return this.candidateFiguresAtModelPoint( modelPoint, true );
  },

  candidateFiguresAtModelPoint( modelPoint, raw = false ) {
    const { selectableIslands } = this;
    const candidates = [];
    const modelIsland = this.modelIslandAt(modelPoint);
    const docIslandCandidates = [];
    const { pages } = this.doc;
    for( let p = pages.length-1; p >= 0; --p ) {
      const page = pages[p];

      if( raw || !page._locked && !page._hidden ) {

        const pagedDesignLayers = designLayersFrom(page);
        const designLayers = raw ? pagedDesignLayers : filterTargetLayers( pagedDesignLayers );

        let figures;
        if( raw ) {
          figures = Layers.flatten(designLayers);
        }
        else {
          figures = [];
          for( let l = 0, lEnd = designLayers.length; l < lEnd; ++l ) {
            this.filterSelectableInto( designLayers[l].figures, figures );
          }
        }


        const docIslands = filterIslandsFor( docIslandsFrom(page), modelIsland );

        for( let f = figures.length-1; f >= 0 ; --f ) {
          const figure = figures[f];

          for( let i = docIslands.length-1; i >= 0 ; --i ) {
            const docIsland = docIslands[i];
            const docPoint = modelToDocMatrix(docIsland,modelIsland).apply( modelPoint );

            if( figure.globalFrame_().contains(docPoint) ) {
              candidates.push({
                figure, docIsland, docPoint,
                tight: raw ? figure.containsPoint(docPoint) : false
              });
            }
          }
        }

        if( raw || selectableIslands ) {
          for( let i = docIslands.length-1; i >= 0 ; --i ) {
            const docIsland = docIslands[i];
            const docPoint = modelToDocMatrix(docIsland,modelIsland).apply( modelPoint );
            docIslandCandidates.push({
              figure: docIsland, docIsland, docPoint,
              tight: raw ? docIsland.containsPoint(docPoint) : false
            });
          }
        }
      }
    }

    return candidates.concat(docIslandCandidates);
  },

  on_domEvent({ event, type, isPrimary }) {

    if( isPrimary && type === 'pointerdown' ) {
      const { modelPoint } = event;
      if( modelPoint && this.modelIslandAt(modelPoint) ) {
        // track3D.log(`[3D] Start tracking of ${type}`);
        const docIsland = this.topDocIslandAtModel(modelPoint);
        if( docIsland ) {
          this.tracker = this.createEventTracker( event, docIsland );
        }
      }
    }

    this.callParent(event);

    if( isPrimary && this.tracker && type === 'pointerup' ) {
      // track3D.log(`[3D] End tracking of ${type}`);
      this.tracker = null;
    }
  },

  handlePluginDomEvent( plugin, { event, type, route, isPrimary } ) {

    if( this.pluginDirectDomEventHandling(plugin) ) {
      plugin.handleDomEvent(event);
      return;
    }

    // We avoid providing feedback in case of 3D views, as it is slowing down
    // things for complex models. It is also not clear how to handle properly
    // feedback in tools if there are several islands. 
    if( false && type === 'mousemove' && ! this.tracker ) {

      const { modelPoint } = event;
      const modelIsland = modelPoint && this.modelIslandAt(modelPoint);
      if( modelIsland ) {
        const docIslands = this.docIslandsFor(modelIsland);
        for( let i = docIslands.length-1; i >= 0 && ! event.stopped; --i ) {
          const docIsland = docIslands[i];
          const newEvent = retargetEvent3D(event,docIsland);
          plugin.on_domEvent(newEvent);
          if( newEvent.stopped ){
            event.stopPropagation();
          }
        }
      }
    }

    if( type === 'pointerdown' && isPrimary ) {

      const { modelPoint } = event;
      const modelIsland = modelPoint && this.modelIslandAt(modelPoint);
      if( modelIsland ) {

        if( plugin.has_try_pointerdown() ){

          const docIslands = this.docIslandsFor(modelIsland);

          for( let i = docIslands.length-1; i >= 0 && ! event.stopped; --i ) {
            const docIsland = docIslands[i];

            const tracker = this.createEventTracker( event, docIsland );
            const newEvent = tracker.retargetedEvent;
            // track3D.log(`[3D] Try to start Tracking of ${type}`);
            plugin.try_pointerdown(newEvent);
            if( newEvent.stopped ){
              this.tracker = tracker; // Change the default tracking to follow this island
              event.stopPropagation();
            }
          }

          if( event.stopped ){
            return false;
          }
        }

        // track3D.log(`[3D] ${type}`);
        plugin.on_domEvent( event );
      }
    }
    else if( ( type === 'pointermove' ||
               type === 'pointerup'    ) && isPrimary ) {

      if( this.tracker ) { // even if modelPoint is null, we track these events
        const newEvent = this.tracker.move(event);
        plugin.on_domEvent(newEvent);
        if( newEvent.stopped ) {
          event.stopPropagation();
        }
      }
    }
    else if( ( type === 'click'       ||
               type === 'dblcick'     ||
               type === 'contextmenu'  ) ) {

      const { modelPoint } = event;
      const modelIsland = modelPoint && this.modelIslandAt(modelPoint);
      if( modelIsland ) {

        const tryHandler = 'try_'+type;
        if( plugin['has_'+tryHandler]() ){


          const docIslands = this.docIslandsFor(modelIsland);
          for( let i = docIslands.length-1; i >= 0 && ! event.stopped; --i ) {
            const docIsland = docIslands[i];
            // track3D.log(`[3D] Retarget of ${type}, using try_`);
            const newEvent = retargetEvent3D(event,docIsland);
            plugin[tryHandler]( newEvent );
            if( newEvent.stopped ){
              event.stopPropagation();
            }
          }

          if( event.stopped ){
            return false;
          }
        }
        // track3D.log(`[3D] Direct routing of ${type}`);
        plugin.on_domEvent(event);
      }
    }
    else if( type === 'keypress'   ||
             type === 'keydown'    ||
             type === 'keyup'      ||
             type === 'mousewheel' ||
             type === 'focus'      ||
             type === 'focusout'   ) {

      // track3D.log(`[3D] Direct routing of ${type}`);
      plugin.on_domEvent(event);
    }
  },

};



/** WEBPACK FOOTER **
 ** ./src/ui/canvas3d/doc3d-model-mixin.js
 **/